import { useQueries, useQuery } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { create, keyResolver, windowedFiniteBatchScheduler } from "@yornaath/batshit";
import { BATSHIT_URL_SAFE_MAX_BATCH_SIZE, BATSHIT_WINDOW_MS, BROKEN_IMAGE_SRC, FIVE_MINUTES_MS } from "@remi/constants";
import { formatPersonContactName, splitContactName } from "@remi/formatting";
import { createId } from "@remi/ts-utils";

import { api, proxyApi } from "~/utils/api";
import { getQueryConfig } from "~/utils/query-config";
import { useFilesByIds } from "./files/useFile";

export const usePersonUpdate = () => {
  const utils = api.useUtils();
  const update = api.person.updatePerson.useMutation({
    onMutate: async (person) => {
      await utils.person.byId.cancel(person.id);
      const previousValueById = utils.person.byId.getData(person.id);
      const phones = person.phones
        ? person.phones.map(({ id, phoneNotes, number }) => ({
            id: id || createId(),
            number,
            phoneNotes: phoneNotes ?? null,
            proxyPhone: null,
          }))
        : [];
      const tags = person.tags?.map((tag) => ({ id: createId(), tag, personId: person.id })) ?? [];
      if (previousValueById) {
        // Mirror the server-side dual-write into the optimistic cache so
        // Contact-sourced reads render the new value instantly instead of
        // flashing the prior Contact.* until the refetch completes. Source
        // the missing-half name from the existing Contact.name split — the
        // partial input may carry only firstName or only lastName.
        const [existingFirstName, existingLastName] = splitContactName(previousValueById.contact.name);
        const optimisticContact = {
          ...previousValueById.contact,
          name:
            person.firstName !== undefined || person.lastName !== undefined
              ? formatPersonContactName(person.firstName ?? existingFirstName, person.lastName ?? existingLastName)
              : previousValueById.contact.name,
          preferredContactMethod:
            person.preferredContactMethod !== undefined
              ? person.preferredContactMethod
              : previousValueById.contact.preferredContactMethod,
          preferredLanguage:
            person.preferredLanguage !== undefined
              ? person.preferredLanguage
              : previousValueById.contact.preferredLanguage,
        };
        utils.person.byId.setData(person.id, {
          ...previousValueById,
          ...person,
          phones,
          tags,
          contact: optimisticContact,
        });
      }
      return { previousValueById };
    },
    onError: (_, variables, context) => {
      if (context?.previousValueById) {
        utils.person.byId.setData(variables.id, context.previousValueById);
      }
    },
    onSettled: async (data, _, input) => {
      if (data === undefined) {
        await utils.person.byId.invalidate(input.id);
        return;
      }
      const { id, conversationIds } = data;
      void utils.person.byId.invalidate(id);
      conversationIds.forEach((conversationId) => void utils.communication.getConversation.invalidate(conversationId));
    },
  });
  return update;
};

// Hoisted to module scope so Math.random() runs once. Per-render calls produce
// a new staleTime value each render; React Query's shallowEqualObjects detects
// the changed value, fires observerOptionsUpdated, and resets the stale
// timeout — causing a refetch loop that cascades into avatar flickering.
const PERSON_QUERY_CONFIG = getQueryConfig(FIVE_MINUTES_MS);

const personBatcher = create({
  fetcher: (ids: string[]) => {
    return proxyApi.person.byIds.query(ids);
  },
  resolver: keyResolver("id"),
  scheduler: windowedFiniteBatchScheduler({
    windowMs: BATSHIT_WINDOW_MS,
    maxBatchSize: BATSHIT_URL_SAFE_MAX_BATCH_SIZE,
  }),
});

export const usePerson = (personId?: string | null) => {
  const queryKey = getQueryKey(api.person.byId, personId ?? undefined);

  return useQuery({
    queryKey,
    queryFn: () => {
      if (!personId) {
        return null;
      }
      return personBatcher.fetch(personId).then((result) => result.person);
    },
    ...PERSON_QUERY_CONFIG,
    enabled: !!personId,
  });
};

export const usePersons = (personIds: string[] = []) => {
  const dedupedIds = Array.from(new Set(personIds));
  const personQueries = useQueries({
    queries: dedupedIds.map((id) => ({
      queryKey: getQueryKey(api.person.byId, id),
      queryFn: () => personBatcher.fetch(id).then((result) => result.person),
      ...PERSON_QUERY_CONFIG,
      enabled: !!id,
    })),
  });
  const persons = personQueries.map((query) => query.data);
  return persons;
};

export const usePersonProfilePhoto = (personId?: string | null) => {
  const { data, ...query } = usePersonProfilePhotos(personId ? [personId] : []);
  return { data: data[0], ...query };
};

export const usePersonProfilePhotos = (personIds: string[] = []) => {
  const dedupedIds = Array.from(new Set(personIds));
  const data = usePersons(dedupedIds);
  const persons = data.filter((d) => !!d);
  const { data: files, ...query } = useFilesByIds(
    persons.map((person) => person.profilePhotoId).filter((id): id is string => !!id),
  );
  const fileMap = new Map(files.map((file) => [file.id, file]));
  const personsWithPhotos = persons.map((person) => {
    const photo = person.profilePhotoId ? fileMap.get(person.profilePhotoId) : undefined;
    return {
      ...person,
      profilePhotoUrl: photo?.thumbnailUrl ?? photo?.url ?? BROKEN_IMAGE_SRC,
    };
  });
  return {
    data: personsWithPhotos,
    ...query,
  };
};
